<template>
  <div class="api-panel">
    <div class="api-panel-head">
      <div class="api-panel-title">
        <h3>{{ detail.name || apiName }}</h3>
        <span class="badge get" v-if="reqMethod">{{ reqMethod.toUpperCase() }}</span>
        <span class="badge json" v-if="respFormat">{{ respFormat.toUpperCase() }}</span>
        <span class="api-price" v-if="price">¥{{ price }}</span>
      </div>
      <router-link v-if="showId && docLink" class="api-panel-doc" :to="docHref">查看完整文档 →</router-link>
    </div>

    <div class="api-meta">
      <div class="row"><b>接口 ID</b><span>{{ showId || '—' }}</span></div>
      <div class="row"><b>请求方式</b><span>{{ reqMethod ? reqMethod.toUpperCase() : '—' }}</span></div>
      <div class="row"><b>返回格式</b><span>{{ respFormat ? respFormat.toUpperCase() : '—' }}</span></div>
      <div class="row"><b>请求地址</b><span class="mono">{{ domainUrl || '—' }}</span></div>
    </div>

    <!-- 登录门禁：未登录时展示提示条，点击「立即测试」也会唤起登录弹窗 -->
    <div class="api-locktip" v-if="!logged">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
      <span>接口测试为<b>登录用户</b>专享，请先
        <a href="javascript:;" @click="openAuth('login')">登录</a> 或
        <a href="javascript:;" @click="openAuth('register')">注册</a> 后使用</span>
    </div>

    <!-- ① 选择请求密钥（套餐类型 + 请求密钥 + 复制 + 剩余次数） -->
    <div class="api-step"><i>1</i>选择请求密钥</div>
    <div class="api-key-bar">
      <div class="api-key-cell mode">
        <label>套餐类型</label>
        <select v-model="billingMode" @change="onModeChange" :disabled="!logged">
          <option v-for="m in modes" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
      </div>
      <div class="api-key-cell key">
        <label>请求密钥</label>
        <select v-model="orderId" @change="onKeyChange($event.target.value)" :disabled="!logged">
          <option value="">请选择密钥</option>
          <option v-for="k in keys" :key="k.value" :value="k.value">{{ k.miyao }}</option>
        </select>
      </div>
      <button class="api-copy" type="button" :disabled="!currentSecret" @click="copySecret">复制</button>
      <div class="api-key-info">
        <span class="api-key-times" v-if="orderId">剩余次数 <b>{{ times }}</b></span>
        <span class="api-key-order" v-if="orderId">{{ orderId }}</span>
      </div>
    </div>
    <p class="api-key-err" v-if="keyErr">{{ keyErr }}</p>
    <p class="api-key-hint" v-if="!logged">登录后可选择名下密钥；密钥用于请求头 <code>secretkey</code> 鉴权。</p>

    <!-- ② 填写请求参数 -->
    <div class="api-step"><i>2</i>填写请求参数</div>
    <div class="api-fields">
      <div class="api-field" v-for="(p, i) in params" :key="p.name">
        <label>
          <em v-if="isRequired(p)">*</em> {{ p.name }}
          <span class="api-field-type">{{ p.desc }}</span>
        </label>
        <input
          type="text"
          v-model="form[i]"
          :class="{ 'is-error': fieldError }"
          :placeholder="isRequired(p) ? '必填' : '选填，留空则不提交'"
          @input="fieldError = ''"
        />
      </div>
      <p class="api-field-err" v-if="paramErr">{{ paramErr }}</p>
    </div>

    <div class="api-actions">
      <button class="btn btn-primary api-test-btn" type="button" @click="run">立即测试</button>
    </div>

    <div class="api-req">
      <template v-if="tested">
        <div><b>请求方式：</b>{{ reqMethod ? reqMethod.toUpperCase() : '' }}</div>
        <div><b>请求地址：</b><code>{{ requestUrl }}</code></div>
        <div><b>请求参数：</b><code>{{ queryText }}</code></div>
        <div><b>请求 secret：</b><code>{{ maskedSecret }}</code></div>
      </template>
      <template v-else>
        <div><b>请求方式：</b>点击「立即测试」后展示请求信息</div>
      </template>
    </div>

    <div class="api-out-head">
      <b>返回内容</b>
      <button class="api-copy" type="button" @click="copyOut">复制</button>
    </div>
    <div class="api-doc" v-html="outputHtml"></div>
  </div>
</template>

<script>
import copy from 'copy-to-clipboard';
import { portalApi, orderApi, gatewayApi } from '@/api';
import { GATEWAY_SCHEME } from '@/config';

/* 右侧在线测试面板
   结构与交互形态参考 website-handoff/js/api-test.js 的 DSSApiPanel（步骤 ① 选密钥 → ② 填参数 → 立即测试 → 请求信息 → 返回内容），
   但**接口与参数完全沿用本项目原有实现**：
     portal/api/interface/details  取接口详情（含 reqParamPretty / domainUrl）
     order/secret                  按套餐类型取名下密钥
     order/invoke/left/num         取该订单剩余次数
     https://{domainUrl}           真实网关调用，请求头 secretkey
   未登录时的行为与原实现一致：提交时 commit('setShowLogin', 1) 唤起登录弹窗。
   注：父级若已取到详情，可通过 apiDetail 注入，此时不再重复请求 portal/api/interface/details
       （接口详情页 /apiDetail 的「在线测试」页签即用此方式，保持页面请求数与旧版一致）。 */
const MODES = [
  { value: 3, label: '包年调用' },
  { value: 1, label: '充值调用' },
  { value: 2, label: '包天调用' }
];

export default {
  name: 'ApiPanel',
  props: {
    api: { type: Object, default: null },
    logged: { type: Boolean, default: false },
    // 父级已取到的接口详情（注入后 loadDetail 不再发请求）
    apiDetail: { type: Object, default: null },
    // 是否显示「查看完整文档 →」入口（在详情页内部使用时隐藏）
    docLink: { type: Boolean, default: true }
  },
  data() {
    return {
      modes: MODES,
      billingMode: 3,
      detail: {},
      domainUrl: '',
      params: [],
      form: {},
      keys: [],
      orderId: '',
      secretLabel: '',
      times: 0,
      keyErr: '',
      paramErr: '',
      fieldError: '',
      respExample: '',
      tested: false,
      requestUrl: '',
      queryText: ''
    };
  },
  computed: {
    showId() {
      return this.detail.showId || '';
    },
    reqMethod() {
      return this.detail.reqMethod || '';
    },
    respFormat() {
      return this.detail.respFormat || '';
    },
    price() {
      return this.detail.price || '';
    },
    apiName() {
      return (this.api && this.api.name) || '';
    },
    docHref() {
      const id = (this.api && this.api.id) || '';
      const dimId = (this.api && this.api.dimId) || '';
      return `/apiDetail?dimId=${dimId}&id=${id}`;
    },
    currentSecret() {
      return this.secretLabel || '';
    },
    maskedSecret() {
      return this.desensitization(this.secretLabel, 6, -6);
    },
    outputHtml() {
      if (!this.respExample) {
        return '<span class="c">// 返回内容将在这里展示</span>';
      }
      const text = JSON.stringify(this.respExample, null, 4);
      return this.highlight(text);
    }
  },
  watch: {
    api() {
      this.loadDetail();
    },
    apiDetail() {
      this.loadDetail();
    }
  },
  mounted() {
    this.loadDetail();
    if (this.logged) this.onModeChange();
  },
  methods: {
    /* 与原实现一致：保留首尾字符、中间以 * 遮盖 */
    desensitization(str, begin, end) {
      if (!str) return '';
      const len = str.length;
      const firstStr = str.substr(0, begin);
      const lastStr = str.substr(end);
      const middleStr = str.substring(begin, len - Math.abs(end)).replace(/[\s\S]/gi, '*');
      return firstStr + middleStr + lastStr;
    },
    isRequired(p) {
      return String((p && p.fill) || '').indexOf('是') > -1;
    },
    openAuth(view) {
      window.dispatchEvent(new CustomEvent('dts:auth-open', {
        detail: { view: view || 'login', notice: '接口测试为登录用户专享，请先登录或注册' }
      }));
    },
    // 把详情写入面板状态（注入与请求两条来源共用）
    applyDetail(data) {
      const detail = data || {};
      this.detail = detail;
      this.domainUrl = detail.domainUrl || '';
      try {
        this.params = JSON.parse(detail.reqParamPretty || '[]');
      } catch (e) {
        this.params = [];
      }
      this.form = {};
      this.respExample = '';
      this.tested = false;
      this.paramErr = '';
      this.fieldError = '';
    },
    loadDetail() {
      // 父级已提供详情时直接使用，避免重复请求
      if (this.apiDetail && Object.keys(this.apiDetail).length) {
        this.applyDetail(this.apiDetail);
        return;
      }
      const id = (this.api && this.api.id) || '';
      if (!id) return;
      portalApi.getInterfaceDetail({ id: id })
        .then((res) => {
          this.applyDetail(res.data);
        })
        .catch(err => {
          this.$message.error(err);
        });
    },
    onModeChange() {
      const user = this.$cookies.getJSON('user');
      if (!(user && user.userId)) return;
      this.orderId = '';
      this.secretLabel = '';
      this.keys = [];
      this.times = 0;
      const params = {
        userId: user.userId,
        billingMode: this.billingMode
      };
      orderApi.getSecrets(params)
        .then((res) => {
          this.solveArr(res.data);
        })
        .catch(err => {
          this.$message.error(err);
        });
    },
    solveArr(arr) {
      if (!arr || arr.length === 0) {
        this.keys = [];
        return;
      }
      const list = [];
      arr.forEach((item, index) => {
        list.push({
          value: item.orderId,
          label: item.secret,
          miyao: '密钥' + (index + 1)
        });
      });
      this.keys = list;
    },
    onKeyChange(val) {
      /* 原生 <select> 的 change 事件参数是 Event（旧实现用 el-select，@change 直接给出 value），
         这里统一取出真实值，避免把事件对象当成 orderId 发给后端 */
      const value = (val && typeof val === 'object' && val.target) ? val.target.value : val;
      this.keyErr = '';
      this.keys.forEach(item => {
        // 原生 select 的 value 恒为字符串，这里按字符串比较（后端返回的 orderId 是数字）
        if (String(item.value) === String(value)) {
          this.secretLabel = item.label;
        }
      });
      this.orderId = value;
      this.getTimes(value);
    },
    getTimes(orderId) {
      if (!orderId) return;
      orderApi.getLeftTimes({ orderId: orderId })
        .then((res) => {
          if (res.state === 200) {
            this.times = res.data;
          } else {
            this.$message.error(res.message);
          }
        })
        .catch(err => {
          this.$message.error(err);
        });
    },
    copySecret() {
      if (!this.currentSecret) return;
      copy(this.currentSecret);
      this.$message.success('密钥已复制');
    },
    copyOut() {
      if (!this.respExample) return;
      copy(JSON.stringify(this.respExample, null, 4));
      this.$message.success('返回内容已复制');
    },
    run() {
      const user = this.$cookies.getJSON('user');
      if (!(user && user.userId)) {
        // 与原实现一致：未登录提交时唤起登录弹窗
        this.$store.commit('setShowLogin', 1);
        return;
      }
      if (!this.orderId) {
        this.keyErr = '请先选择请求密钥（secretkey）';
        return;
      }
      this.keyErr = '';
      let hasEmptyRequired = false;
      const newObj = {};
      for (let i = 0; i < this.params.length; i++) {
        const name = this.params[i].name;
        newObj[name] = this.form[i];
        if (!this.form[i] && this.isRequired(this.params[i])) {
          hasEmptyRequired = true;
        }
      }
      if (hasEmptyRequired) {
        this.paramErr = '请先输入请求参数';
        this.fieldError = '1';
        return;
      }
      this.paramErr = '';
      this.fieldError = '';
      this.respExample = '';

      const query = Object.keys(newObj)
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(newObj[k] === undefined ? '' : newObj[k])}`)
        .join('&');
      this.requestUrl = `${GATEWAY_SCHEME}://${this.domainUrl}${query ? '?' + query : ''}`;
      this.queryText = JSON.stringify(newObj);

      // 真实网关调用（地址与请求头口径与原实现一致）
      gatewayApi.callApi(this.domainUrl, newObj, this.secretLabel)
        .then((res) => {
          this.respExample = res;
          this.tested = true;
          this.getTimes(this.orderId);
        })
        .catch(err => {
          this.$message.error(err);
        });
    },
    escapeHtml(str) {
      return String(str).replace(/[&<>"]/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
      }[c]));
    },
    highlight(text) {
      return this.escapeHtml(text)
        .replace(/("[^"]*")(\s*:)/g, '<span class="k">$1</span>$2')
        .replace(/:\s*("[^"]*")/g, ': <span class="s">$1</span>')
        .replace(/:\s*(-?\d+)/g, ': <span class="n">$1</span>');
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/ApiPanel.vue